import { AxiosError, AxiosResponse } from 'axios';
import { makeAutoObservable, reaction, runInAction } from 'mobx';

export enum UploadStatus {
  INITIAL = 'INITIAL',
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  FAIL = 'FAIL',
  ABORTED = 'ABORTED',
}

type CreateFileFuncSignature = (
  onProgress: (val: number) => void
) => readonly [AbortController, Promise<AxiosResponse<void, any>>];

export class UploadWatcher {
  private _status: UploadStatus;
  private _name: string;
  private _abortController: AbortController | null = null;
  private _promise: Promise<any> | null = null;
  private _error: string = '';

  private _progress = 0;

  public get error() {
    return this._error;
  }
  public get status() {
    return this._status;
  }
  public get name() {
    return this._name;
  }

  public get progressAsPercent() {
    return this._progress * 100;
  }

  public get isSuccess() {
    return this._status === UploadStatus.SUCCESS;
  }

  private _onSuccess: (id: string) => void;
  private _onError: (id: string, error: string) => void;

  private _startUpload: CreateFileFuncSignature;

  constructor({
    name,
    onSuccess,
    onError,
    startUpload,
  }: {
    name: string;
    onSuccess: (name: string) => void;
    onError: (name: string, error: string) => void;
    startUpload: CreateFileFuncSignature;
  }) {
    this._name = name;
    this._onSuccess = onSuccess;
    this._onError = onError;
    this._startUpload = startUpload;
    this._status = UploadStatus.INITIAL;
    makeAutoObservable(this);

    reaction(
      () => this._status,
      (status) => {
        switch (status) {
          case UploadStatus.SUCCESS:
            this._onSuccess(this.name);
            return;
          case UploadStatus.FAIL:
            this._onError(this.name, this._error);
            return;
          default:
            return;
        }
      }
    );
  }

  public startUpload() {
    this._status = UploadStatus.PENDING;

    [this._abortController, this._promise] = this._startUpload(
      (progress) => (this._progress = progress)
    );

    this._promise.then(
      () => {
        this._status = UploadStatus.SUCCESS;
      },
      (e: AxiosError<{ detail: string }>) => {
        // ABORTED может быть только после отмены пользователем
        // После отмены промис упадет и не должен перетереть состояние
        if (this._status === UploadStatus.ABORTED) {
          return;
        }
        runInAction(() => {
          this._error = e.response?.data?.detail || '';
          this._status = UploadStatus.FAIL;
        });
      }
    );
  }

  public abort() {
    this._promise = null;
    this._abortController && this._abortController.abort();
    this._status = UploadStatus.ABORTED;
  }
}
